import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

const CurrencyContext = createContext(null);

const SUPPORTED = ['KES', 'USD', 'GBP', 'EUR'];
const DEFAULT_SYMBOLS = 'USD,GBP,EUR';

function getApiBase() {
  return import.meta.env.VITE_API_URL || 'http://localhost:4080/api/v1';
}

async function fetchFxRates() {
  const url = `${getApiBase()}/fx/rates?base=KES&symbols=${encodeURIComponent(DEFAULT_SYMBOLS)}`;
  const res = await fetch(url, { headers: { Accept: 'application/json' } });
  if (!res.ok) {
    throw new Error(`Failed to fetch FX rates (${res.status})`);
  }
  const json = await res.json();
  // integration-service returns { success, data: { base, rates, asOf, provider } }
  const data = json.data || json;
  return {
    base: data.base || 'KES',
    rates: data.rates || {},
    asOf: data.asOf || new Date().toISOString(),
    provider: data.provider || 'unknown',
    meta: json.meta || {},
  };
}

export function CurrencyProvider({ children }) {
  const [currency, setCurrency] = useState(() => {
    const saved = localStorage.getItem('bomatrust.currency');
    return SUPPORTED.includes(saved) ? saved : 'KES';
  });
  const [fx, setFx] = useState({ base: 'KES', rates: {}, asOf: null, provider: null });
  const [fxLoading, setFxLoading] = useState(true);
  const [fxError, setFxError] = useState(null);

  useEffect(() => {
    localStorage.setItem('bomatrust.currency', currency);
  }, [currency]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setFxLoading(true);
        setFxError(null);
        const data = await fetchFxRates();
        if (!cancelled) {
          setFx({ base: data.base, rates: data.rates, asOf: data.asOf, provider: data.provider });
        }
      } catch (err) {
        if (!cancelled) {
          setFxError(err.message || 'Failed to load FX rates');
        }
      } finally {
        if (!cancelled) setFxLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const isSupported = useCallback(code => SUPPORTED.includes(code), []);

  const convertFromKES = useCallback(
    (amountKes, targetCurrency) => {
      const target = String(targetCurrency || 'KES').toUpperCase();
      if (target === 'KES') return amountKes;
      const rate = fx.rates?.[target];
      if (!rate || typeof rate !== 'number') return null;
      return amountKes * rate;
    },
    [fx.rates],
  );

  const value = useMemo(
    () => ({
      currency,
      setCurrency,
      supportedCurrencies: SUPPORTED,
      isSupported,
      fx,
      fxLoading,
      fxError,
      convertFromKES,
    }),
    [currency, fx, fxError, fxLoading, isSupported, convertFromKES],
  );

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

CurrencyProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// eslint-disable-next-line react-refresh/only-export-components
export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error('useCurrency must be used within CurrencyProvider');
  return ctx;
}
